import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
	CanActivate,
	ActivatedRouteSnapshot,
	RouterStateSnapshot,
	UrlTree,
	Router,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { delay, filter, switchMap, take } from 'rxjs/operators';

import { AuthService, CartService, CheckoutModeService } from '@core/services';
import { UserState } from '@shared/interfaces';
import { isPlatformServer, isPlatformBrowser } from '@angular/common';
import { environment } from '@env/environment';

@Injectable({
	providedIn: 'root',
})
export class AccountGuard implements CanActivate {
	constructor(
		private cartService: CartService,
		private authService: AuthService,
		private checkoutMode: CheckoutModeService,
		private router: Router,
		@Inject(PLATFORM_ID) private platformId: any
	) {}
	
	canActivate(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot
	): Observable<boolean | UrlTree> {
		const isMyAccount = state.url.includes('my-account');
		const isLogin = state.url.includes('login');
		const isCheckout = state.url.includes('checkout');
		const isServer = isPlatformServer(this.platformId);
		const isBrowser = isPlatformBrowser(this.platformId);
		const navigationState =
			this.router.getCurrentNavigation()?.extras.state ||
			(isBrowser ? history.state : {});
		const isSampleOrderConfirmation = !!navigationState?.sampleOrderConfirmation;
		if (!!isPlatformServer(this.platformId)) {
			return of(true);
		}
		// // On server, always allow
		// if (isServer) {
		// 	return of(true);
		// }

		// Save previous URL
		if (isBrowser) {
			let url = this.router.url;
			sessionStorage.setItem('previousUrl', environment.canonicalBaseUrl + url);
		}

		return this.authService.getUserState$().pipe(
			delay(250),
			take(1),
			switchMap((userState: UserState) => {
				console.log('[AccountGuard] Current userState:', userState);
				this.authService.storeUserState(userState);
				return !!userState
					? of(userState)
					: this.authService.updateUserState$(
							this.authService.baseUserState,
							'[AUTH GUARD]'
					  );
			}),
			switchMap((userState: UserState) => {
				const refreshToken = this.authService.timeToRefreshToken(
					userState?.token_gen
				);

				if (!!refreshToken) {
					return this.authService.getAuthToken$().pipe(take(1));
				}

				return of(userState);
			}),
			switchMap((userState: UserState | any) => {
				if (isMyAccount && userState?.anonymous) {
					return of(this.router.createUrlTree(['/login']));
				}
				if (
					isCheckout &&
					!isSampleOrderConfirmation &&
					userState?.anonymous &&
					!this.checkoutMode.isGuestCheckout
				) {
					return of(this.router.createUrlTree(['/login']));
				}
				if (isLogin && !userState?.anonymous) {
					return of(this.router.createUrlTree(['/my-account']));
				}
					if (isCheckout && isBrowser && !isSampleOrderConfirmation) {
    return this.cartService.getCartState$().pipe(
        filter(state => state !== null), 
        take(1),
        switchMap(cartState => {
            const cartItems = cartState?.allCartItems || [];
            if (!cartItems.length) {
                return of(this.router.createUrlTree(['/my-cart']));
            }
            return of(true);
        })
    );
}

				if (isBrowser && state.url.includes('reset-password')) {
					const hasSession = !!sessionStorage.getItem('password_expired_session');
					if (!hasSession) {
						return of(this.router.createUrlTree(['/login']));
					}
				}

				return of(true);
			})
		);
	}
}
